import { createRef } from "react";
import { pushFreeSearchEvent, pushModuleBuyNowLinkTracking, pushNullSearchEvent, } from "@discovery/common-dplus/lib/adobe";
import { clientInformation } from "@discovery/common-dplus/lib/device-info";
import { envBrowser } from "@discovery/maestro-env-browser";
import * as E from "fp-ts/Either";
import * as App from "@discovery/maestro";
import { useEffect, useGlobalState, useState, } from "@discovery/common-dplus/lib/hooks";
import { _Config, _IsUserPremiumFromPartners, } from "@discovery/common-dplus/lib/state";
import { config } from "@discovery/common-dplus/lib/utils/config-types";
import { Fold, Lens } from "@discovery/prelude/lib/control/lens";
import * as M from "@discovery/prelude/lib/data/maybe";
import { menuBarNavigations } from "../../../../component-creators/menu-bar";
import { _ShowsCollectionFromSonicApi, _VideosCollectionFromSonicApi, } from "../../../../component-creators/optics";
import { navigateTo, useDebounce } from "../../../../utils/common";
import { useUser } from "../../../../utils/hooks/useUser";
import { CircularSpinner } from "../../../atoms/circular-spinner";
import { Icon, Icons } from "../../../atoms/icon";
import { Input } from "../../../atoms/input";
import { Link } from "../../../atoms/link";
import { HeaderVariants, Tags, Typography } from "../../../atoms/typography";
import { Navigation } from "../../navigation";
import { EmptySearchResults } from "../../../molecules/empty-search-results";
import { getVideosEndpointWithSearchQuery, getShowsEndpointDefault, } from "../../../../utils/api/sonicApiEndpoints";
import { SearchResultsDropDown } from "../search-results-dropdown";
import * as styles from "../../nav-bar/styles.css";
import { setAuthRedirectUrl } from "../../../../utils/authUrl";
import { blueshiftClickEvent } from "@discovery/common-dplus/lib/blueshift";
const hideSearchResults = createRef();
const hideSearchResultsInput = createRef();
export const HomeRoute = Lens.prop("homeRoute");
export const DesktopSearch = ({ loginUrl, read }) => {
    const [configM] = useGlobalState(_Config);
    const [isPremiumUserFromPartner] = useGlobalState(_IsUserPremiumFromPartners);
    const { isPremiumUser, isAnonymous, userId } = useUser();
    const premiumPackagesM = M.chain((x) => x.premiumPackages, configM);
    const premiumPackages = M.fromMaybe(premiumPackagesM, [""]);
    const parentalLockM = M.chain((x) => x.parentalLock, configM);
    const minimumAgeM = M.chain((x) => x.minimumAge, parentalLockM);
    const ageConstraint = M.fromMaybe(minimumAgeM, 0);
    const subscriptionCTA = M.chain((x) => x.subscriptionCTA, configM);
    const subscriptionCTALabelM = M.chain((x) => x.label, subscriptionCTA);
    const subscriptionCTAUrlM = M.chain((x) => x.url, subscriptionCTA);
    const subscriptionCTALabel = M.fromMaybe(subscriptionCTALabelM, "");
    const subscriptionCTAUrl = M.fromMaybe(subscriptionCTAUrlM, "");
    const debounce = -1;
    const [searchText, setSearchText] = useState("");
    const debouncedSearchTerm = useDebounce(searchText, 300);
    const [showSearchComponentForWeb, setSearchComponentForWeb] = useState(false);
    const [searchResultsLength, setSearchResultsLength] = useState(0);
    const [counter, setCounter] = useState(0);
    const [showLoader, setShowLoader] = useState(false);
    const [searchResultsForWeb, setSearchResultsForWeb] = useState([]);
    const isUserPremium = isPremiumUser || isPremiumUserFromPartner;
    const [navBarOptions, setNavBarOptions] = useState([]);
    const clearSearchValue = () => {
        setSearchComponentForWeb(false);
        setSearchText("");
    };
    const getSearchResults = ([Shows, Episodes, Shorts]) => [
        {
            title: "Shows",
            type: "Shows",
            cardData: Fold.toArray(_ShowsCollectionFromSonicApi, Shows),
        },
        {
            title: "Episodes",
            type: "Episodes",
            cardData: Fold.toArray(_VideosCollectionFromSonicApi, Episodes),
        },
        {
            title: "Shorts",
            type: "Clip",
            cardData: Fold.toArray(_VideosCollectionFromSonicApi, Shorts),
        },
    ];
    const closeSearch = (e) => {
        var _a, _b;
        if ((_a = hideSearchResults.current) === null || _a === void 0 ? void 0 : _a.contains(e.target)) {
            return;
        }
        if (!((_b = hideSearchResultsInput.current) === null || _b === void 0 ? void 0 : _b.contains(e.target))) {
            clearSearchValue();
        }
    };
    const fetchShows = (searchText) => {
        const endpoint = getShowsEndpointDefault(encodeURIComponent(searchText), "isFavorite", 10);
        return App.run(endpoint)(Object.assign(Object.assign(Object.assign({}, envBrowser), read), { clientInformation: clientInformation })).then(E.fold((_exception) => {
            /* handle exception here */
        }, (response) => {
            return response;
        }));
    };
    const fetchEpisodes = (searchText) => {
        const filterParam = {
            type: "filterType1",
            filterParamtype1: "videoType",
            filterParamValue1: "EPISODE",
        };
        const endpoint = getVideosEndpointWithSearchQuery("viewingHistory", filterParam, 10, 1, encodeURIComponent(searchText));
        return App.run(endpoint)(Object.assign(Object.assign(Object.assign({}, envBrowser), read), { clientInformation: clientInformation })).then(E.fold((_exception) => {
            /* handle exception here */
        }, (response) => {
            return response;
        }));
    };
    const fetchShorts = (searchText) => {
        const filterParam = {
            type: "filterType1",
            filterParamtype1: "videoType",
            filterParamValue1: "CLIP",
        };
        const endpoint = getVideosEndpointWithSearchQuery("viewingHistory", filterParam, 10, 1, encodeURIComponent(searchText));
        return App.run(endpoint)(Object.assign(Object.assign(Object.assign({}, envBrowser), read), { clientInformation: clientInformation })).then(E.fold((_exception) => {
            /* handle exception here */
        }, (response) => {
            return response;
        }));
    };
    useEffect(() => {
        if (debouncedSearchTerm.length >= 1) {
            document.addEventListener("mousedown", closeSearch);
            window.addEventListener("popstate", clearSearchValue);
            setShowLoader(true);
            let length;
            Promise.all([
                fetchShows(debouncedSearchTerm),
                fetchEpisodes(debouncedSearchTerm),
                fetchShorts(debouncedSearchTerm),
            ]).then((data) => {
                const result = getSearchResults(data);
                length = result.map((item) => {
                    return item.cardData.length;
                });
                const reducer = (accumulator, currentValue) => accumulator + currentValue;
                setSearchComponentForWeb(true);
                setSearchResultsForWeb(result);
                setShowLoader(false);
                setSearchResultsLength(length.reduce(reducer));
                setCounter(counter + 1);
                pushFreeSearchEvent(debouncedSearchTerm, length.reduce(reducer), counter > 0 ? "true" : "false");
                length.reduce(reducer) === 0 &&
                    pushNullSearchEvent(debouncedSearchTerm);
            });
        }
        else {
            window.removeEventListener("popstate", clearSearchValue);
            setSearchComponentForWeb(false);
            setSearchResultsForWeb([]);
            clearTimeout(debounce);
        }
        return function cleanUp() {
            window.removeEventListener("popstate", clearSearchValue);
            document.removeEventListener("mousedown", closeSearch);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearchTerm]);
    useEffect(() => {
        const options = menuBarNavigations.map((item) => {
            if (item.url === config.standardPageRouteFragments.premium) {
                if (isUserPremium) {
                    return item;
                }
                else {
                    item.url = config.standardPageRouteFragments.goPremium;
                    return item;
                }
            }
            else {
                return item;
            }
        });
        setNavBarOptions(options);
    }, [isUserPremium]);
    const onLoginPress = () => {
        const authRedirect = setAuthRedirectUrl(window.location.pathname);
        isAnonymous
            ? navigateTo(`${loginUrl + "/login"}?returnUrl=${authRedirect}`)
            : navigateTo(`${loginUrl + "/my-account"}?returnUrl=${authRedirect}`);
    };
    const renderSignIn = () => {
        return (React.createElement(React.Fragment, null, isAnonymous ? (React.createElement("div", { className: styles.login, onClick: onLoginPress, id: "#signIn" },
            React.createElement(Typography, { classNameM: M.of(styles.signIn), variant: HeaderVariants.headingSix, tag: Tags.h6 },
                React.createElement(React.Fragment, null, "Sign In")))) : (React.createElement(React.Fragment, null,
            React.createElement("div", { className: styles.signInContainer, onClick: onLoginPress, id: "#signIn" },
                isUserPremium && (React.createElement(Link, { href: config.standardPageRouteFragments.home },
                    React.createElement(Icon, { type: Icons.HeaderPremium, className: styles.premiumAccountLogo, color: "white" }))),
                React.createElement(Typography, { classNameM: M.of(styles.signIn), variant: HeaderVariants.headingSix, tag: Tags.h6 },
                    React.createElement(React.Fragment, null, "My Account")))))));
    };
    const renderBuyNow = () => {
        const sendAnalyticsEvent = () => {
            pushModuleBuyNowLinkTracking("home", 0, "toolbar", subscriptionCTALabel, subscriptionCTAUrl);
        };
        const actionItems = () => {
            blueshiftClickEvent("home", "toolbar", 0, subscriptionCTALabel, subscriptionCTAUrl, userId);
            sendAnalyticsEvent();
        };
        return (React.createElement("div", { className: styles.signInContainer, onClick: actionItems, id: "#buyNow" },
            React.createElement(Link, { href: subscriptionCTAUrl },
                React.createElement(Typography, { classNameM: M.of(styles.signIn), variant: HeaderVariants.headingSix, tag: Tags.h6 },
                    React.createElement(React.Fragment, null, subscriptionCTALabel)))));
    };
    const renderNavbar = () => {
        return (React.createElement(React.Fragment, null,
            React.createElement(Navigation, { navigationItems: navBarOptions }),
            React.createElement("div", { className: styles.castContainer },
                React.createElement("div", { className: styles.searchContainer },
                    React.createElement("div", { ref: hideSearchResultsInput },
                        React.createElement(Input, { valM: M.of(searchText), placeholdeM: M.of("Search for a show, episode, shorts etc."), typeM: M.of("text"), idM: M.of("searchInput"), autofocus: false, classNameM: M.of(styles.searchInputDesktop), onInputChange: (e) => setSearchText(e.target.value.trimLeft()) })),
                    searchText ? (React.createElement("div", { onClick: clearSearchValue, className: searchText ? styles.clearIcon : styles.clearIconDisable, id: "clearSearchValue" },
                        !showLoader && React.createElement(Icon, { type: Icons.CloseSearch }),
                        showLoader && (React.createElement("div", { className: styles.circularSpinner },
                            React.createElement(CircularSpinner, null))))) : null,
                    !searchText && (React.createElement("div", { className: styles.castContainerWeb, id: "#searchIcon" },
                        React.createElement(Icon, { type: Icons.Search }))),
                    showSearchComponentForWeb && searchResultsForWeb.length && (React.createElement("div", { className: styles.searchWrapperDesktop, id: "searchResultsWeb", ref: hideSearchResults },
                        React.createElement("div", { id: "#searchCardContainer", onClick: clearSearchValue },
                            searchResultsForWeb.map((item, index) => (React.createElement(SearchResultsDropDown, { title: item.title, data: item.cardData.slice(0, 1), isSearchWeb: true, debouncedSearchTerm: debouncedSearchTerm, searchResultsLength: searchResultsLength, key: index, premiumPackages: premiumPackages, ageConstraint: ageConstraint }))),
                            searchResultsLength == 0 && (React.createElement(EmptySearchResults, { origin: "desktop" }))),
                        React.createElement("div", { className: styles.searchResultsSeparotor }),
                        searchResultsLength !== 0 && (React.createElement(Link, { href: `/search?q=${searchText}` },
                            React.createElement("div", { className: styles.viewAllWrapper, onClick: clearSearchValue },
                                React.createElement(Typography, { classNameM: M.of(styles.viewAllSearch), variant: HeaderVariants.headingSix, tag: Tags.h6 },
                                    React.createElement(React.Fragment, null, "View All")))))))),
                renderSignIn(),
                !isUserPremium &&
                    (window.location.pathname === "/" ||
                        window.location.pathname === "/home") &&
                    renderBuyNow()),
            React.createElement("div", { className: styles.searchForMobile },
                React.createElement(Link, { href: config.standardPageRouteFragments.search, classNameM: M.of(styles.IconWrapper) },
                    React.createElement(Icon, { type: Icons.Search })),
                renderSignIn(),
                !isUserPremium &&
                    (window.location.pathname === "/" ||
                        window.location.pathname === "/home") &&
                    renderBuyNow())));
    };
    return renderNavbar();
};

